/**
 * Fetch a URL through a public CORS proxy and parse Open Graph meta tags
 * client-side. No API key required.
 *
 * Tries multiple proxies in order — some sites (Houzz, Pinterest, etc.) use
 * CloudFlare bot protection that blocks individual proxies, so we fall back
 * through several. If all fail, returns empty preview rather than throwing,
 * so the link still gets added (user can edit the image manually).
 *
 * Returns { imageUrl, title, description }.
 */

const PROXIES = [
  // corsproxy.io — returns the raw response body
  (url) => ({
    endpoint: `https://corsproxy.io/?${encodeURIComponent(url)}`,
    extract: (res) => res.text(),
  }),
  // codetabs — returns raw body
  (url) => ({
    endpoint: `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`,
    extract: (res) => res.text(),
  }),
  // allorigins — returns JSON with `contents` field
  (url) => ({
    endpoint: `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
    extract: async (res) => (await res.json()).contents || "",
  }),
];

async function fetchHtmlViaProxies(url) {
  const errors = [];
  for (const make of PROXIES) {
    const { endpoint, extract } = make(url);
    try {
      const res = await fetch(endpoint);
      if (!res.ok) {
        errors.push(`${endpoint} → ${res.status}`);
        continue;
      }
      const html = await extract(res);
      if (html && html.length > 100) return html;
      errors.push(`${endpoint} → empty body`);
    } catch (err) {
      errors.push(`${endpoint} → ${err.message}`);
    }
  }
  console.warn("[linkPreview] all proxies failed", errors);
  return null;
}

/**
 * Parse a pasted HTML embed snippet (e.g. Houzz "share" embed) and pull out
 * the first image src + first link href + alt text. Lets users bypass scraper
 * blocks by pasting an embed directly.
 *
 * Returns { imageUrl, sourceUrl, title } or null if no <img> found.
 */
export function parseEmbedSnippet(html) {
  if (!html || typeof html !== "string") return null;
  if (!/<img[\s>]/i.test(html)) return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const img = doc.querySelector("img");
    if (!img) return null;
    const imageUrl = img.getAttribute("src") || "";
    if (!imageUrl) return null;
    const anchor = doc.querySelector("a[href]");
    const sourceUrl = anchor?.getAttribute("href") || imageUrl;
    const title = img.getAttribute("alt") || anchor?.textContent?.trim() || "";
    return { imageUrl, sourceUrl, title };
  } catch {
    return null;
  }
}

export async function fetchLinkPreview(url) {
  const html = await fetchHtmlViaProxies(url);
  if (!html) {
    // All proxies failed — return empty preview so the link still gets saved
    return { imageUrl: "", title: "", description: "" };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  const getMeta = (selectors) => {
    for (const sel of selectors) {
      const el = doc.querySelector(sel);
      const content = el?.getAttribute("content");
      if (content) return content;
    }
    return "";
  };

  let imageUrl = getMeta([
    'meta[property="og:image:secure_url"]',
    'meta[property="og:image"]',
    'meta[name="twitter:image"]',
    'meta[name="twitter:image:src"]',
    'link[rel="image_src"]',
  ]);

  if (imageUrl) {
    try {
      imageUrl = new URL(imageUrl, url).href;
    } catch {
      /* leave as-is */
    }
  }

  const title =
    getMeta(['meta[property="og:title"]', 'meta[name="twitter:title"]']) ||
    doc.querySelector("title")?.textContent?.trim() ||
    "";

  const description = getMeta([
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
    'meta[name="description"]',
  ]);

  return { imageUrl, title, description };
}
